import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Activity,
  AlertCircle,
  BarChart3,
  ExternalLink,
  Eye,
  Filter,
  MapPin,
  MousePointerClick,
  RefreshCw,
  Smartphone,
  TrendingDown,
  UserPlus,
  Users,
  X,
} from 'lucide-react';

import { RankedTable, Tile } from '@/components/analytics/widgets';
import { api } from '@/services/api';
import { WebAnalytics, WebSourceFilter, WebSourceRow } from '@/types';

/**
 * First-party web analytics.
 *
 * Every number here comes from our own web_events table: patient-web posts
 * non-PHI step events to /api/v1/events on every route, including the funnel,
 * auth and payment.
 *
 * The traffic-source filter (?source=&medium=&campaign=&touch= in the URL)
 * narrows every number on the page to one source's visitors, e.g. the VFS QR
 * code: vfs / qr / sfo_office.
 */

// Matches web_events.NO_VALUE: a medium/campaign that was not tagged.
const NO_VALUE = '(none)';
const FILTER_PARAMS = ['source', 'medium', 'campaign', 'touch'] as const;

function filterFromRow(row: WebSourceRow, touch: WebSourceFilter['touch']): WebSourceFilter {
  return { source: row.source, medium: row.medium ?? NO_VALUE, campaign: row.campaign ?? NO_VALUE, touch };
}

/** "vfs / qr / sfo_office", leaving out untagged parts. */
function sourceLabel(f: { source: string; medium?: string | null; campaign?: string | null }): string {
  return [f.source, f.medium, f.campaign].filter((v) => v && v !== NO_VALUE).join(' / ');
}

function sourceKey(f: { source: string; medium?: string | null; campaign?: string | null }): string {
  return JSON.stringify([f.source, f.medium ?? NO_VALUE, f.campaign ?? NO_VALUE]);
}

function rowMatches(row: WebSourceRow, f: WebSourceFilter | null): boolean {
  if (!f || row.source !== f.source) return false;
  if (f.medium !== undefined && (row.medium ?? NO_VALUE) !== f.medium) return false;
  if (f.campaign !== undefined && (row.campaign ?? NO_VALUE) !== f.campaign) return false;
  return true;
}

const WINDOW_OPTIONS: { value: number | 'all'; label: string }[] = [
  { value: 1, label: 'Last 24 hours' },
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
];

export default function WebAnalyticsPage() {
  const [data, setData] = useState<WebAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [days, setDays] = useState<number | 'all'>(30);
  const [includeInternal, setIncludeInternal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // Sources seen on the last unfiltered load, for the source picker.
  const [sourceOptions, setSourceOptions] = useState<WebSourceRow[]>([]);

  const sourceFilter = useMemo<WebSourceFilter | null>(() => {
    const source = searchParams.get('source');
    if (!source) return null;
    return {
      source,
      medium: searchParams.get('medium') ?? undefined,
      campaign: searchParams.get('campaign') ?? undefined,
      touch: searchParams.get('touch') === 'any' ? 'any' : 'first',
    };
  }, [searchParams]);
  const filterKey = FILTER_PARAMS.map((k) => searchParams.get(k) ?? '').join('|');

  const setSourceFilter = (f: WebSourceFilter | null) => {
    const next = new URLSearchParams(searchParams);
    FILTER_PARAMS.forEach((k) => next.delete(k));
    if (f) {
      next.set('source', f.source);
      if (f.medium !== undefined) next.set('medium', f.medium);
      if (f.campaign !== undefined) next.set('campaign', f.campaign);
      if (f.touch === 'any') next.set('touch', 'any');
    }
    setSearchParams(next, { replace: true });
  };

  // The picker lists every source seen unfiltered, plus the current filter.
  const pickerOptions = useMemo(() => {
    const options = [...sourceOptions];
    if (sourceFilter && !options.some((o) => rowMatches(o, sourceFilter))) {
      options.unshift({
        source: sourceFilter.source,
        medium: sourceFilter.medium ?? null,
        campaign: sourceFilter.campaign ?? null,
        sessions: 0,
        paid: 0,
      });
    }
    return options;
  }, [sourceOptions, sourceFilter]);
  const activeOption = pickerOptions.find((o) => rowMatches(o, sourceFilter));

  const fetchData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const res: WebAnalytics = await api.getWebAnalytics({
        days,
        ...(includeInternal ? { internal: 1 as const } : {}),
        ...(sourceFilter
          ? {
              source: sourceFilter.source,
              medium: sourceFilter.medium,
              campaign: sourceFilter.campaign,
              touch: sourceFilter.touch,
            }
          : {}),
      });
      setData(res);
      if (!sourceFilter) {
        // Remember every source (first touch + this session) for the picker.
        const seen = new Map<string, WebSourceRow>();
        for (const row of [...res.sources, ...(res.lastTouchSources ?? [])]) {
          const key = sourceKey(row);
          const prev = seen.get(key);
          if (!prev || row.sessions > prev.sessions) seen.set(key, row);
        }
        setSourceOptions([...seen.values()].sort((a, b) => b.sessions - a.sessions));
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load web analytics');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, includeInternal, filterKey]);

  const noData = !data || data.totals.events === 0;
  const paid = data?.journey.find((s) => s.key === 'payment_success')?.count ?? 0;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Web Analytics</h1>
          <p className="text-gray-500 mt-1">
            What visitors do on voyaxhealth.com, from landing page to payment (first-party, no PHI)
          </p>
        </div>
        <div className="flex items-center gap-3 mt-4 sm:mt-0">
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-gray-400" />
            <select
              value={String(days)}
              onChange={(e) => {
                const v = e.target.value;
                setDays(v === 'all' ? 'all' : Number(v));
              }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {WINDOW_OPTIONS.map((o) => (
                <option key={String(o.value)} value={String(o.value)}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-gray-600" title="Team browsers are flagged by visiting the site once with ?internal=1">
            <input
              type="checkbox"
              checked={includeInternal}
              onChange={(e) => setIncludeInternal(e.target.checked)}
              className="rounded border-gray-300"
            />
            Include team traffic
          </label>
          <button
            onClick={fetchData}
            className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw size={18} className="mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Traffic source filter: narrows every number below */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <label htmlFor="source-filter" className="text-sm font-medium text-gray-700 shrink-0">
            Traffic source
          </label>
          <select
            id="source-filter"
            value={activeOption ? sourceKey(activeOption) : ''}
            onChange={(e) => {
              const row = pickerOptions.find((o) => sourceKey(o) === e.target.value);
              setSourceFilter(row ? filterFromRow(row, sourceFilter?.touch ?? 'first') : null);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm md:min-w-[18rem] focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All traffic</option>
            {pickerOptions.map((o) => (
              <option key={sourceKey(o)} value={sourceKey(o)}>
                {sourceLabel(o)}
                {o.sessions ? ` (${o.sessions.toLocaleString()} ${o.sessions === 1 ? 'session' : 'sessions'})` : ''}
              </option>
            ))}
          </select>
          <label htmlFor="source-touch" className="text-sm font-medium text-gray-700 shrink-0 md:ml-2">
            Credit by
          </label>
          <select
            id="source-touch"
            value={sourceFilter?.touch ?? 'first'}
            disabled={!sourceFilter}
            onChange={(e) => sourceFilter && setSourceFilter({ ...sourceFilter, touch: e.target.value as WebSourceFilter['touch'] })}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="first">First visit</option>
            <option value="any">Any visit</option>
          </select>
          {sourceFilter && (
            <button
              onClick={() => setSourceFilter(null)}
              className="inline-flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              <X size={16} className="mr-1" />
              Clear
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {sourceFilter ? (
            <>
              Every number below counts only visitors{' '}
              {sourceFilter.touch === 'first' ? 'whose first visit came from' : 'with at least one visit from'}{' '}
              <span className="font-semibold text-gray-900">{sourceLabel(sourceFilter)}</span>, including what they did
              on later visits.
            </>
          ) : (
            <>
              Pick a source, or click a row under Traffic sources, to see how far those visitors got. “First visit”
              credits the source that first brought someone; “Any visit” also counts people who came back through it.
            </>
          )}
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-3">
            <AlertCircle className="text-red-500" size={20} />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : error ? null : noData ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <BarChart3 className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            {sourceFilter ? `No visitors from ${sourceLabel(sourceFilter)} yet` : 'No web activity yet'}
          </h3>
          <p className="mt-2 text-gray-500">
            {sourceFilter
              ? 'No one from this source was recorded in this time window. Try a longer window or “Any visit”.'
              : 'No events were recorded in this time window. Events arrive from the patient site a few seconds after each page view or click.'}
          </p>
        </div>
      ) : (
        <>
          {/* Summary tiles */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <Tile icon={Users} tint="bg-primary-100 text-primary-600" label="Visitors" value={data.totals.visitors.toLocaleString()} />
            <Tile icon={Activity} tint="bg-blue-100 text-blue-600" label="Sessions" value={data.totals.sessions.toLocaleString()} />
            <Tile icon={Eye} tint="bg-amber-100 text-amber-600" label="Page views" value={data.totals.pageViews.toLocaleString()} />
            <Tile icon={MousePointerClick} tint="bg-purple-100 text-purple-600" label="Events" value={data.totals.events.toLocaleString()} />
            <Tile icon={TrendingDown} tint="bg-green-100 text-green-600" label="Paid visitors" value={paid.toLocaleString()} />
          </div>

          {/* Journey */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-sm font-semibold text-gray-900 mb-1">Visitor journey</h2>
            <p className="text-xs text-gray-500 mb-5">
              Distinct visitors who reached each step in the window, in the order a traveller
              experiences them. A visitor can skip steps (for example, sign in instead of sign up).
            </p>
            <div className="space-y-4">
              {data.journey.map((step, idx) => (
                <div key={step.key}>
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-sm font-medium text-gray-900">
                      {idx + 1}. {step.label}{' '}
                      <span className="text-xs font-normal text-gray-400">({step.event})</span>
                    </span>
                    <span className="text-sm text-gray-600">
                      <span className="font-semibold text-gray-900">{step.count.toLocaleString()}</span>{' '}
                      ({step.pctOfStart}% of landed)
                    </span>
                  </div>
                  <div className="relative h-6 bg-gray-100 rounded-md overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-primary-500 rounded-md transition-all"
                      style={{ width: `${Math.max(step.pctOfStart, 1)}%` }}
                    />
                  </div>
                  {idx > 0 && (
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                      <TrendingDown size={13} className="text-red-400" />
                      <span>{step.droppedFromPrevious.toLocaleString()} fewer than the previous step</span>
                      <span className="text-gray-300">•</span>
                      <span>{step.pctOfPrevious}% of previous</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Accounts by source: saved at sign-up, progress from the database */}
          <div className="mb-6">
            <RankedTable
              title="Accounts by source (from sign-up)"
              icon={UserPlus}
              rows={data.accountsBySource ?? []}
              empty="No accounts created in this window."
              subtitle="Accounts created in the window · progress from the database"
              onRowClick={(r) =>
                r.source !== '(unknown)' &&
                setSourceFilter({ source: r.source, medium: r.medium ?? NO_VALUE, campaign: r.campaign ?? NO_VALUE, touch: 'first' })
              }
              isRowActive={(r) => !!sourceFilter && r.source === sourceFilter.source && (r.medium ?? NO_VALUE) === (sourceFilter.medium ?? NO_VALUE) && (r.campaign ?? NO_VALUE) === (sourceFilter.campaign ?? NO_VALUE)}
              columns={[
                { header: 'Source', render: (r) => r.source },
                { header: 'Medium', render: (r) => r.medium ?? '—' },
                { header: 'Campaign', render: (r) => r.campaign ?? '—' },
                { header: 'Accounts', render: (r) => r.accounts.toLocaleString(), align: 'right' },
                { header: 'Confirmed', render: (r) => r.confirmed.toLocaleString(), align: 'right' },
                { header: 'Consultation', render: (r) => r.consultations.toLocaleString(), align: 'right' },
                { header: 'Paid', render: (r) => r.paid.toLocaleString(), align: 'right' },
                {
                  header: 'Revenue',
                  render: (r) => r.revenue.toLocaleString(undefined, { style: 'currency', currency: 'USD' }),
                  align: 'right',
                },
              ]}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <RankedTable
              title="Traffic sources (first touch)"
              icon={MapPin}
              rows={data.sources}
              empty="No sessions recorded."
              subtitle="Click a row to filter"
              onRowClick={(r) => setSourceFilter(filterFromRow(r, 'first'))}
              isRowActive={(r) => sourceFilter?.touch === 'first' && rowMatches(r, sourceFilter)}
              columns={[
                { header: 'Source', render: (r) => r.source },
                { header: 'Medium', render: (r) => r.medium ?? '—' },
                { header: 'Campaign', render: (r) => r.campaign ?? '—' },
                { header: 'Sessions', render: (r) => r.sessions.toLocaleString(), align: 'right' },
                { header: 'Paid', render: (r) => r.paid.toLocaleString(), align: 'right' },
              ]}
            />
            <RankedTable
              title="Landing pages"
              icon={Eye}
              rows={data.landingPages}
              empty="No landing pages recorded."
              columns={[
                { header: 'Path', render: (r) => r.path },
                { header: 'Sessions', render: (r) => r.sessions.toLocaleString(), align: 'right' },
              ]}
            />
            <RankedTable
              title="Top pages"
              icon={BarChart3}
              rows={data.topPages}
              empty="No page views recorded."
              columns={[
                { header: 'Route', render: (r) => r.route },
                { header: 'Views', render: (r) => r.views.toLocaleString(), align: 'right' },
                { header: 'Visitors', render: (r) => r.visitors.toLocaleString(), align: 'right' },
              ]}
            />
            <RankedTable
              title="Top events"
              icon={MousePointerClick}
              rows={data.topEvents}
              empty="No events other than page views."
              columns={[
                { header: 'Event', render: (r) => r.event },
                { header: 'Count', render: (r) => r.count.toLocaleString(), align: 'right' },
                { header: 'Visitors', render: (r) => r.visitors.toLocaleString(), align: 'right' },
              ]}
            />
            <RankedTable
              title="Devices"
              icon={Smartphone}
              rows={data.devices}
              empty="No sessions recorded."
              columns={[
                { header: 'Device', render: (r) => r.device },
                { header: 'Sessions', render: (r) => r.sessions.toLocaleString(), align: 'right' },
              ]}
            />
            <RankedTable
              title="Turned away by state (California-only)"
              icon={AlertCircle}
              rows={data.ineligibleStates}
              empty="No out-of-state sign-ups in this window."
              columns={[
                { header: 'State', render: (r) => r.state },
                { header: 'Sign-ups', render: (r) => r.count.toLocaleString(), align: 'right' },
              ]}
            />
            <RankedTable
              title="Traffic sources (this session)"
              icon={MapPin}
              rows={data.lastTouchSources ?? []}
              empty="No sessions recorded."
              subtitle="Click a row to filter"
              onRowClick={(r) => setSourceFilter(filterFromRow(r, 'any'))}
              isRowActive={(r) => sourceFilter?.touch === 'any' && rowMatches(r, sourceFilter)}
              columns={[
                { header: 'Source', render: (r) => r.source },
                { header: 'Medium', render: (r) => r.medium ?? '—' },
                { header: 'Campaign', render: (r) => r.campaign ?? '—' },
                { header: 'Sessions', render: (r) => r.sessions.toLocaleString(), align: 'right' },
                { header: 'Paid', render: (r) => r.paid.toLocaleString(), align: 'right' },
              ]}
            />
            <RankedTable
              title="Partner clicks"
              icon={ExternalLink}
              rows={data.partnerClicks ?? []}
              empty="No partner clicks in this window."
              columns={[
                { header: 'Partner', render: (r) => r.partner },
                { header: 'Clicks', render: (r) => r.clicks.toLocaleString(), align: 'right' },
                { header: 'Visitors', render: (r) => r.visitors.toLocaleString(), align: 'right' },
              ]}
            />
          </div>

          {/* Engagement: are the content pages actually read? */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <RankedTable
              title="Reading depth by page"
              icon={Eye}
              rows={data.engagement ?? []}
              empty="Not enough page views yet (needs 3+ per page)."
              columns={[
                { header: 'Route', render: (r) => r.route },
                { header: 'Views', render: (r) => r.views.toLocaleString(), align: 'right' },
                { header: 'Avg time', render: (r) => `${r.avgSeconds}s`, align: 'right' },
                { header: 'Read ≥50%', render: (r) => `${r.pctScrolledHalf}%`, align: 'right' },
                { header: 'Read ≥90%', render: (r) => `${r.pctScrolledMost}%`, align: 'right' },
              ]}
            />
            <RankedTable
              title="Errors visitors hit"
              icon={AlertCircle}
              rows={data.errors ?? []}
              empty="No errors recorded. Good."
              columns={[
                { header: 'Type', render: (r) => r.kind },
                { header: 'Where', render: (r) => r.where },
                { header: 'Detail', render: (r) => r.detail ?? '' },
                { header: 'Count', render: (r) => r.count.toLocaleString(), align: 'right' },
              ]}
            />
            <RankedTable
              title="Backend failures (server_errors)"
              icon={AlertCircle}
              rows={data.serverErrors ?? []}
              empty="No backend failures recorded. Good."
              columns={[
                { header: 'Lambda', render: (r) => r.lambda.replace(/^voyahealth-/, '').replace(/-(dev|prod)$/, '') },
                { header: 'Endpoint', render: (r) => `${r.endpoint} → ${r.status}` },
                { header: 'Error', render: (r) => r.errorClass ?? '' },
                { header: 'Count', render: (r) => r.count.toLocaleString(), align: 'right' },
              ]}
            />
            <RankedTable
              title="Page speed (Core Web Vitals, 75th percentile)"
              icon={Activity}
              rows={data.webVitals ?? []}
              empty="No speed samples yet."
              columns={[
                { header: 'Metric', render: (r) => r.name },
                { header: 'Samples', render: (r) => r.samples.toLocaleString(), align: 'right' },
                { header: 'p75', render: (r) => (r.name === 'CLS' ? r.p75.toFixed(3) : `${Math.round(r.p75)} ms`), align: 'right' },
                { header: 'Good', render: (r) => `${r.pctGood}%`, align: 'right' },
              ]}
            />
          </div>

          <p className="text-xs text-gray-400">
            Generated {new Date(data.generatedAt).toLocaleString()} ·{' '}
            {data.windowDays == null
              ? 'All time'
              : `Last ${data.windowDays} day${data.windowDays === 1 ? '' : 's'}`}
            {data.includesInternal
              ? ' · including team traffic'
              : data.internalEventsExcluded > 0
                ? ` · ${data.internalEventsExcluded.toLocaleString()} team events excluded`
                : ''}
          </p>
        </>
      )}
    </div>
  );
}
